<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells><h1 class="title">Stocktaking's</h1></app-cells>
      <app-cells v-if="showCreateBtn">
        <app-button @click.native="$modal.show('create-modal')">Create stocktaking</app-button>
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__id>
          <div class="table-default__left">ID</div>
        </template>
        <template #id="{ row }">
          <div class="table-default__left">
            <router-link :to="{name: 'product-storage-stocktaking-detail', params: {id: row.id }}" class="table-default__link-border">
              Stocktaking - {{ row.id }}
            </router-link>
          </div>
        </template>
        <template #date="{ row }">
          {{ dateFormat(row.date) }}
        </template>
        <template #products_increase="{ row }">
          {{row.products_increase.toLocaleString() }} TSh
        </template>
        <template #products_decrease="{ row }">
          {{row.products_decrease.toLocaleString() }} TSh
        </template>
        <template #products_balance="{ row }">
          {{row.products_balance.toLocaleString() }} TSh
        </template>
        <template #status="{ row }">
          <span class="status-tag" :style="{ 'backgroundColor': row.status.color || '#8a8a8a' }">
            {{ row.status.name }}
          </span>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
      name="create-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('create-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">New stocktaking</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Storekeeper" required>
              <app-select
                v-model="form.storekeeper"
                :options="storekeeper_options"
                :reduce="item => item.id"
                :clearable="false"
                label="full_name"
                placeholder="Storekeeper"
                :error="$v.form.storekeeper.$error"
              />
              <template #error>
                <div v-if="$v.form.storekeeper.$dirty && !$v.form.storekeeper.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Supervisor" required>
              <app-select
                @search="onSupervisorSearch"
                v-model="form.supervisor"
                :options="supervisor_options"
                :clearable="false"
                label="full_name"
                placeholder="Supervisor"
                :error="$v.form.supervisor.$error"
              />
              <template #error>
                <div v-if="$v.form.supervisor.$dirty && !$v.form.supervisor.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">Create stocktaking</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getEmployeesListSearch, getProductStorageStocktaking, postProductStorageStocktaking } from '@/http'
import { format, parse } from 'date-fns'
import { required } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'

export default {
  name: 'ProductStorageStocktakingList',
  data() {
    return {
      is_loaded: false,
      table: {
        items: [],
        columns: ['id', 'date', 'products_increase', 'products_decrease', 'products_balance', 'status'],
        options: {
          headings: {
            'id': 'ID',
            'date': 'Date',
            'products_increase': 'Value increase',
            'products_decrease': 'Value decrease',
            'products_balance': 'Balance',
            'status': 'Status',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      form: {
        storekeeper: null
      },
      storekeeper_options: [],
      supervisor_options: [],
      table_preloader: false
    }
  },
  validations: {
    form: {
      storekeeper: { required },
      supervisor: { required },
    }
  },
  computed: {
    showCreateBtn() {
      return this.$store.state.userGroups.some(group => group.code === 'general_manager' || group.code === 'product_storage_supervisor')
    }
  },
  created() {
    this.fetchItems()
    getEmployeesListSearch({
      position: 'product_keeper',
      employment_status: 'working',
    })
      .then(response => {
        this.storekeeper_options = response.data
        if (response.data.length) this.form.storekeeper = response.data[0].id
      })
  },
  methods: {
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      postProductStorageStocktaking(this.normalizeForm())
        .finally(() => { this.$refs.submit.preload = false })
        .then(response => {
          this.$notify({
            type: 'success',
            title: 'Stocktaking created',
          })
          this.$router.push({ name: 'product-storage-stocktaking-detail', params: { id: response.data.id } })
        })
    },
    normalizeForm() {
      const normalizedForm = { ...this.form }

      normalizedForm.supervisor = normalizedForm.supervisor.id

      return normalizedForm
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
    },
    fetchItems() {
      getProductStorageStocktaking({ page: this.pagination.page, page_size: 20 })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.is_loaded = true
          this.table_preloader = false
        })
    },
    onSupervisorSearch(search, loading) {
      loading(true)
      this.supervisorSearch(loading, search, this)
    },
    supervisorSearch: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.supervisor_options = []
      } else {
        getEmployeesListSearch({ search, employment_status: 'working' }).then(response => {
          vm.supervisor_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>
